<template>
  <div class="sign-up-wrapper" v-if="!isLoading">
    <div class="sign-up-header">
      <img
        class="logo"
        src="../../../public/custom/images/logo.svg"
        alt="Logo image"
        @click="goToMain()"
      />
      <img
        class="header-icon"
        src="../../../public/custom/images/header-icon.svg"
        alt="Header icon"
      />
    </div>
    <div class="sign-up-container">
      <div class="title">{{ $t("message.signUp.title") }}</div>
      <div class="description">{{ $t("message.signUp.description") }}</div>

      <div class="sign-up-block sign-up-block-numbered">
        <div class="sign-up-block-title">
          <div class="step-block">1.</div>
          <div class="step-block-desc">
            {{ $t("message.signUp.role.title") }}
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="large-input-container role-block">
            <div class="role-item" :class="{'role-item--disabled': !canRegisterAsBorrower}">
              <base-radio-button
                :disabled="!canRegisterAsBorrower"
                :checked="userData.role === 'borrower'"
                @click="setRole('borrower')"
              />
              <div class="role-item-desc" @click="setRole('borrower')">
                <div class="role-item-title">
                  {{ $t('message.signUp.roleBlock.borrower.title') }}
                </div>
                <div class="role-item-subtitle">
                  {{ $t('message.signUp.roleBlock.borrower.subtitle') }}
                </div>
              </div>
            </div>
            <div class="role-item">
              <base-radio-button
                :checked="userData.role === 'lender'"
                @click="setRole('lender')"
              />
              <div class="role-item-desc" @click="setRole('lender')">
                <div class="role-item-title">
                  {{ $t('message.signUp.roleBlock.lender.title') }}
                </div>
                <div class="role-item-subtitle">
                  {{ $t('message.signUp.roleBlock.lender.subtitle') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="sign-up-block sign-up-block-numbered">
        <div class="sign-up-block-title">
          <div class="step-block">2.</div>
          <div class="step-block-desc">
            {{ $t("message.signUp.mainData.title") }}
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <base-text-input
              :default-value="userData.name_ru.family_name_ru"
              :label="$t('message.signUp.mainData.surname.title')"
              :disabled="signUpDisabledFields.family_name_ru"
              v-model="userData.name_ru.family_name_ru"
              :name="'surname'"
              :validate-rules="'surname'"
            />
          </div>
          <div class="middle-input-container">
            <base-text-input
              :default-value="userData.name_ru.given_name_ru"
              :label="$t('message.signUp.mainData.name.title')"
              :disabled="signUpDisabledFields.given_name_ru"
              v-model="userData.name_ru.given_name_ru"
              :name="'name'"
              :validate-rules="'name'"
            />
          </div>
          <div class="middle-input-container">
            <base-text-input
              :default-value="userData.name_ru.middle_name_ru"
              :label="$t('message.signUp.mainData.middleName.title')"
              :disabled="signUpDisabledFields.middle_name_ru"
              v-model="userData.name_ru.middle_name_ru"
              :name="'middleName'"
              :validate-rules="'middleName'"
            />
          </div>
        </div>

        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <div class="input-wrapper">
              <div class="input-label">
                {{ $t("message.signUp.mainData.birthDate.title") }}
              </div>
              <div class="datepicker-block">
                <BaseDatepicker
                  :is-has-calendar-icon="false"
                  :placeholder="$t('message.signUp.mainData.birthDate.placeholder')"
                  :defaultValue="userData.birthdate"
                  v-model="userData.birthdate"
                  :disabled="signUpDisabledFields.birthdate"
                  :value-type="'YYYYMMDD'"
                  :format="'DD.MM.YYYY'"
                />
              </div>
              <div class="input-error-block"></div>
            </div>
          </div>

          <div class="middle-input-container">
            <BaseSelect
              :label="$t('message.signUp.mainData.citizenship.title')"
              :default-value="userData.citizenship"
              :options-list="CONSTANTS.CITIZENSHIP_TYPE_LIST"
              :dictionary-path="'message.signUp.livingAddressBlock.country.countryList.'"
              v-model="userData.citizenship"
              :disabled="signUpDisabledFields.citizenship"
              :name="'citizenship'"
            />
          </div>

          <div class="middle-input-container">
            <div class="input-wrapper">
              <div class="input-label">
                {{ $t("message.signUp.mainData.gender.title") }}
              </div>
              <div class="gender-block">
                <div class="gender-item">
                  <BaseRadioButton
                    :disabled="true"
                    :checked="userData.sex === 'male'"
                  />
                  <div class="gender-name">{{ $t("message.signUp.mainData.gender.male") }}</div>
                </div>
                <div class="gender-item">
                  <BaseRadioButton
                    :disabled="true"
                    :checked="userData.sex === 'female'"
                  />
                  <div class="gender-name">{{ $t("message.signUp.mainData.gender.female") }}</div>
                </div>
              </div>
              <div class="input-error-block"></div>
            </div>
          </div>
        </div>

        <div class="sign-up-block-container">
          <div class="large-input-container">
            <base-textarea-input
              :disabled="signUpDisabledFields.birth_place_str"
              :default-value="userData.birth_place_str"
              v-model="userData.birth_place_str"
              :label="$t('message.signUp.mainData.birthPlace.title')"
              :placeholder="$t('message.signUp.mainData.birthPlace.placeholder')"
              :name="'birthPlace'"
              :validate-rules="'birthPlace'"
            />
          </div>
        </div>
      </div>

      <div class="sign-up-block sign-up-block-numbered">
        <div class="sign-up-block-title">
          <div class="step-block">3.</div>
          <div class="step-block-desc">
            {{ $t("message.signUp.idBlock.title") }}
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="large-input-container">
            <BaseSelect
              :label="$t('message.signUp.idBlock.mainDocument.title')"
              :default-value="userData.id_document.type"
              :options-list="CONSTANTS.MAIN_DOCUMENT_TYPE_LIST"
              :dictionary-path="'message.signUp.idBlock.mainDocument.list.'"
              v-model="userData.id_document.type"
              :disabled="signUpDisabledFields.type"
              :name="'mainDocument'"
            />
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <base-text-input
              :default-value="userData.national_id_number"
              :label="$t('message.signUp.idBlock.idIdentification.title')"
              :disabled="signUpDisabledFields.national_id_number"
              v-model="userData.national_id_number"
              :name="'nationalIdNumber'"
              :validate-rules="'nationalIdNumber'"
            />
          </div>

          <div class="middle-input-container">
            <base-text-input
              :default-value="userData.id_document.seriesNumber"
              :label="$t('message.signUp.idBlock.seriesAndNumber.title')"
              :disabled="signUpDisabledFields.seriesNumber"
              v-model="userData.id_document.seriesNumber"
              :name="'idSeries'"
              :validate-rules="'idSeries'"
            />
          </div>

          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <div class="input-wrapper">
              <div class="input-label">
                {{ $t("message.signUp.idBlock.idDateIssue.title") }}
              </div>
              <div class="datepicker-block">
                <BaseDatepicker
                  :is-has-calendar-icon="false"
                  :placeholder="$t('message.signUp.idBlock.idDateIssue.placeholder')"
                  :defaultValue="userData.id_document.issueDate"
                  v-model="userData.id_document.issueDate"
                  :disabled="signUpDisabledFields.issueDate"
                  :value-type="'YYYYMMDD'"
                  :format="'DD.MM.YYYY'"
                />
              </div>
              <div class="input-error-block"></div>
            </div> 
          </div>

          <div class="middle-input-container">
            <div class="input-wrapper">
              <div class="input-label">
                {{ $t("message.signUp.idBlock.expireDate.title") }}
              </div>
              <div class="datepicker-block">
                <BaseDatepicker
                  :is-has-calendar-icon="false"
                  :placeholder="$t('message.signUp.idBlock.expireDate.placeholder')"
                  :defaultValue="userData.id_document.expireDate"
                  v-model="userData.id_document.expireDate"
                  :disabled="signUpDisabledFields.expireDate"
                  :value-type="'YYYYMMDD'"
                  :format="'DD.MM.YYYY'"
                />
              </div>
              <div class="input-error-block"></div>
            </div> 
          </div>

          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container">
          <div class="large-input-container">
            <base-textarea-input
              :disabled="signUpDisabledFields.authority"
              :default-value="userData.id_document.authority"
              v-model="userData.id_document.authority"
              :label="$t('message.signUp.idBlock.idIssuedBy.title')"
              :placeholder="$t('message.signUp.idBlock.idIssuedBy.placeholder')"
              :name="'idIssuedBy'"
              :validate-rules="'idIssuedBy'"
            />
          </div>
        </div>
      </div>

      <div class="sign-up-block sign-up-block-numbered">
        <div class="sign-up-block-title">
          <div class="step-block">4.</div>
          <div class="step-block-desc">
            {{ $t("message.signUp.addressBlock.title") }}
          </div>
        </div>

        <div class="sign-up-block-container">
          <div class="large-input-container">
            <base-textarea-input
              :disabled="signUpDisabledFields.address_str"
              :default-value="userData.address_str"
              v-model="userData.address_str"
              :label="$t('message.signUp.addressBlock.registrationAdress.title')"
              :name="'registrationAdress'"
              :validate-rules="'registrationAdress'"
            />
          </div>
        </div>

        <div class="sign-up-block-container">
          <div class="large-input-container same-address-container">
            <base-radio-button
              :checked="isAddressLivingAndRegistrationTheSame"
              @click="addressToggle()"
            />
            <div class="same-address-title" @click="addressToggle()">{{ $t('message.signUp.addressBlock.registrationAdress.theSame') }}</div>
          </div>
        </div>

        <template v-if="!isAddressLivingAndRegistrationTheSame">
          <div class="sign-up-block-container">
            <div class="large-input-container">
              <div class="input-label extra-data-radiobtn-input-label mb-20">
                {{ $t('message.signUp.addressBlock.livingAddress.title') }}
              </div>
            </div>
          </div>
          <div class="sign-up-block-container living-address-grid">
            <div
              v-for="fieldKey in livingAddressFieldKeys"
              :key="fieldKey"
              class="living-address-field"
            >
              <base-text-input
                :label="$t(`message.signUp.addressBlock.fields.${fieldKey}`)"
                :placeholder="$t(`message.signUp.addressBlock.livingAddress.placeholders.${fieldKey}`)"
                v-model="living_address[fieldKey]"
                :name="`livingAddress_${fieldKey}`"
                :is-has-error-block="false"
              />
            </div>
          </div>
        </template>
      </div>

      <div class="sign-up-block sign-up-block-numbered">
        <div class="sign-up-block-title">
          <div class="step-block">5.</div>
          <div class="step-block-desc">
            {{ $t("message.signUp.jobBlock.title") }}
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <BaseSelect
              :label="$t('message.signUp.jobBlock.typeOfEmployment.title')"
              :default-value="work_info.employment_type"
              :options-list="CONSTANTS.EMPLOYMENT_TYPE_LIST"
              :dictionary-path="'message.signUp.jobBlock.typeOfEmployment.list.'"
              :placeholder="''"
              v-model="work_info.employment_type"
              :name="'typeOfEmployment'"
            />
          </div>
          <div class="middle-input-container">
            <BaseSelect
              :label="$t('message.signUp.jobBlock.fieldOfEmployment.title')"
              :default-value="work_info.scope_of_employment_type"
              :options-list="CONSTANTS.SCOPE_OF_EMPLOYMENT_TYPE_LIST"
              :dictionary-path="'message.signUp.jobBlock.fieldOfEmployment.list.'"
              :placeholder="''"
              v-model="work_info.scope_of_employment_type"
              :name="'fieldOfEmployment'"
              :disabled="isEmploymentWorkFieldsDisabled"
            />
          </div>
          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container">
          <div class="large-input-container">
            <base-textarea-input
              v-model="work_info.organization_name"
              :default-value="work_info.organization_name"
              :label="$t('message.signUp.jobBlock.nameOfOrganization.title')"
              :placeholder="''"
              :name="'nameOfOrganization'"
              :validate-rules="'nameOfOrganization'"
              :disabled="isEmploymentWorkFieldsDisabled"
            />
          </div>
        </div>

        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <BaseSelect
              :label="$t('message.signUp.jobBlock.post.title')"
              :default-value="work_info.job_position_type"
              :options-list="CONSTANTS.POST_TYPE_LIST"
              :dictionary-path="'message.signUp.jobBlock.post.list.'"
              :placeholder="''"
              v-model="work_info.job_position_type"
              :name="'post'"
              :disabled="isEmploymentWorkFieldsDisabled"
            />
          </div>
          <div class="middle-input-container">
            <base-text-input
              :label="$t('message.signUp.jobBlock.otherPositionType.title')"
              v-model="work_info.job_position_name"
              :default-value="work_info.job_position_name"
              :name="'otherPositionType'"
              :validate-rules="'otherPositionType'"
              :disabled="isEmploymentWorkFieldsDisabled"
              v-if="isOtherJobPositionType"
            />
          </div>
          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container" v-if="userData.role === 'borrower'">
            <div class="middle-input-container">
              <div class="input-wrapper">
                <div class="input-label">
                  {{ $t('message.signUp.jobBlock.workExperience.title') }}
                </div>
                <div class="half-middle-input-block">
                  <base-text-input
                    :placeholder="$t('message.signUp.jobBlock.workExperience.placeholder')"
                    v-model="work_info.workExperience"
                    :default-value="work_info.workExperience"
                    :name="'workExperience'"
                    :validate-rules="'workExperience'"
                    :mask-pattern="'####'"
                    :disabled="isEmploymentWorkFieldsDisabled"
                  />
                </div>
              </div>
            </div>
          <div class="middle-input-container"></div>
        </div>
      </div>

      <div class="sign-up-block sign-up-block-numbered" v-if="userData.role === 'borrower'">
        <div class="sign-up-block-title">
          <div class="step-block">6.</div>
          <div class="step-block-desc">
            {{ $t("message.signUp.extraBlock.title") }}
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <BaseSelect
              :label="$t('message.signUp.extraBlock.education.title')"
              :default-value="extra_user_info.education"
              :options-list="CONSTANTS.EDUCATION_TYPE_LIST"
              :dictionary-path="'message.signUp.extraBlock.education.list.'"
              v-model="extra_user_info.education"
              :name="'education'"
            />
          </div>
          <div class="middle-input-container">
            <BaseSelect
              :label="$t('message.signUp.extraBlock.familyStatus.title')"
              :default-value="extra_user_info.family_status"
              :options-list="CONSTANTS.FAMILY_STATUS_TYPE_LIST"
              :dictionary-path="'message.signUp.extraBlock.familyStatus.list.'"
              v-model="extra_user_info.family_status"
              :name="'familyStatus'"
            />
          </div>
          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container row-radiobtn-container">
          <div class="middle-input-container">
            <div class="input-label extra-data-radiobtn-input-label">
              {{ $t("message.signUp.extraBlock.driversLicense.title") }}
            </div>
          </div>
          <div class="middle-input-container extra-radiobtn-block">
            <div class="role-item">
              <base-radio-button
                :checked="extra_user_info.drivers_license"
                @click="extra_user_info.drivers_license = true"
              />
              <div class="role-item-desc" @click="extra_user_info.drivers_license = true">
                {{ $t('message.signUp.extraBlock.driversLicense.yes') }}
              </div>
            </div>
            <div class="role-item">
              <base-radio-button
                :checked="!extra_user_info.drivers_license"
                @click="extra_user_info.drivers_license = false"
              />
              <div class="role-item-desc" @click="extra_user_info.drivers_license = false">
                {{ $t('message.signUp.extraBlock.driversLicense.no') }}
              </div>
            </div>
          </div>
          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container row-radiobtn-container">
          <div class="middle-input-container">
            <div class="input-label extra-data-radiobtn-input-label">
              {{ $t("message.signUp.extraBlock.car.title") }}
            </div>
          </div>
          <div class="middle-input-container extra-radiobtn-block">
            <div class="role-item">
              <base-radio-button
                :checked="extra_user_info.car_owner"
                @click="setCarOwner(true)"
              />
              <div class="role-item-desc" @click="setCarOwner(true)">
                {{ $t('message.signUp.extraBlock.car.yes') }}
              </div>
            </div>
            <div class="role-item">
              <base-radio-button
                :checked="!extra_user_info.car_owner"
                @click="setCarOwner(false)"
              />
              <div class="role-item-desc" @click="setCarOwner(false)">
                {{ $t('message.signUp.extraBlock.car.no') }}
              </div>
            </div>
          </div>
          <div class="middle-input-container"></div>
        </div>

        <div
          v-if="extra_user_info.car_owner"
          class="sign-up-block-container car-passport-upload-container"
        >
          <div class="large-input-container">
            <SignUpDocumentsUploader
              v-model="car_passport_files"
              :title="$t('message.signUp.extraBlock.car.technicalPassport.title')"
              :hint="$t('message.signUp.extraBlock.car.technicalPassport.hint')"
              :attach-btn="$t('message.signUp.extraBlock.car.technicalPassport.attachBtn')"
              :max-files="4"
            />
          </div>
        </div>

        <div class="sign-up-block-container row-radiobtn-container">
          <div class="middle-input-container">
            <div class="input-label extra-data-radiobtn-input-label">
              {{ $t("message.signUp.extraBlock.realEstate.title") }}
            </div>
          </div>
          <div class="middle-input-container extra-radiobtn-block">
            <div class="role-item">
              <base-radio-button
                :checked="extra_user_info.real_estate_owner"
                @click="setRealEstateOwner(true)"
              />
              <div class="role-item-desc" @click="setRealEstateOwner(true)">
                {{ $t('message.signUp.extraBlock.realEstate.yes') }}
              </div>
            </div>
            <div class="role-item">
              <base-radio-button
                :checked="!extra_user_info.real_estate_owner"
                @click="setRealEstateOwner(false)"
              />
              <div class="role-item-desc" @click="setRealEstateOwner(false)">
                {{ $t('message.signUp.extraBlock.realEstate.no') }}
              </div>
            </div>
          </div>
          <div class="middle-input-container"></div>
        </div>

        <div
          v-if="extra_user_info.real_estate_owner"
          class="sign-up-block-container car-passport-upload-container"
        >
          <div class="large-input-container">
            <SignUpDocumentsUploader
              v-model="real_estate_files"
              :title="$t('message.signUp.extraBlock.realEstate.registrationCertificate.title')"
              :hint="$t('message.signUp.extraBlock.realEstate.registrationCertificate.hint')"
              :attach-btn="$t('message.signUp.extraBlock.realEstate.registrationCertificate.attachBtn')"
              :max-files="5"
            />
          </div>
        </div>

        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <div class="input-wrapper">
              <div class="input-label">
                {{ $t('message.signUp.extraBlock.dependensNumber.title') }}
              </div>
              <div class="half-middle-input-block">
                <base-text-input
                  v-model="extra_user_info.dependens_number"
                  :name="'dependensNumber'"
                  :validate-rules="'dependensNumber'"
                  :mask-pattern="'##'"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="sign-up-block-container row-radiobtn-container">
          <div class="middle-input-container">
            <div class="input-label extra-data-radiobtn-input-label">
              {{ $t("message.signUp.extraBlock.childExpenses.title") }}
            </div>
          </div>
          <div class="middle-input-container extra-radiobtn-block">
            <div class="role-item">
              <base-radio-button
                :checked="extra_user_info.child_expenses"
                @click="extra_user_info.child_expenses = true"
              />
              <div class="role-item-desc" @click="extra_user_info.child_expenses = true">
                {{ $t('message.signUp.extraBlock.childExpenses.yes') }}
              </div>
            </div>
            <div class="role-item">
              <base-radio-button
                :checked="!extra_user_info.child_expenses"
                @click="extra_user_info.child_expenses = false"
              />
              <div class="role-item-desc" @click="extra_user_info.child_expenses = false">
                {{ $t('message.signUp.extraBlock.childExpenses.no') }}
              </div>
            </div>
          </div>
          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <div class="input-wrapper">
              <div class="input-label">
                {{ $t('message.signUp.extraBlock.totalIncome.title') }}
              </div>
              <div class="half-middle-input-block">
                <base-text-input
                  v-model="extra_user_info.total_income"
                  :default-value="extra_user_info.total_income"
                  :name="'totalIncome'"
                  :validate-rules="'totalIncome'"
                  :mask-pattern="'######'"
                  :disabled="isTotalIncomeDisabled"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="sign-up-block sign-up-block-numbered">
        <div class="sign-up-block-title">
          <div class="step-block">{{ userData.role === 'borrower' ? '7.' : '6.' }}</div>
          <div class="step-block-desc">
            {{ $t("message.signUp.basicBlock.title") }}
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="middle-input-container key-word-block">
            <base-text-input
              :label="$t('message.signUp.basicBlock.keyWord.title')"
              v-model="userData.codeword"
              :name="'codeword'"
              :validate-rules="'codeword'"
              :is-have-notice="true"
              :notice-message="$t('message.signUp.basicBlock.keyWord.noticeMsg')"
            />
            <div class="key-word-warning">
              {{ $t('message.signUp.basicBlock.keyWord.warningMsg') }}
            </div>
          </div>
        </div>
        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <base-text-input
              :default-value="userData.email"
              :label="$t('message.signUp.basicBlock.email.title')"
              :disabled="signUpDisabledFields.email"
              v-model="userData.email"
              :name="'email'"
            />
          </div>

          <div class="middle-input-container">
            <base-password-input
              :label="$t('message.signUp.passwordBlock.password.title')"
              v-model="userData.password"
              :validate-rules="'password'"
              :name="'password'"
            />
          </div>

          <div class="middle-input-container"></div>
        </div>

        <div class="sign-up-block-container">
          <div class="middle-input-container">
            <base-text-input
              :label="$t('message.signUp.basicBlock.msisdn.title')"
              v-model="userData.phone_number"
              :default-value="userData.phone_number"
              :disabled="signUpDisabledFields.phone_number"
              :name="'msisdn'"
              :validate-rules="'msisdn'"
              :mask-pattern="'+### (##) ### ## ##'"
            />
          </div>
          <div class="middle-input-container">
            <div class="input-wrapper">
              <div class="input-label">
                {{ $t("message.signUp.basicBlock.otp.title") }}
              </div>
              <div class="otp-block">
                <base-text-input
                  v-model="userData.otp"
                />
              </div>
              <div class="input-error-block send-otp-block" @click="getOtpCode()">
                {{ $t("message.signUp.basicBlock.otp.sendOtp") }}
              </div>
            </div>
          </div>
          <div class="middle-input-container"></div>
        </div>
      </div>

      <div class="sign-up-block sign-up-block-agreements">
        <!-- <div class="offer-item">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isDataFullAndCorrect }"
            @click.stop="offerToggle('isDataFullAndCorrect')"
          >
            <img
              v-if="isDataFullAndCorrect"
              class="check-box-image"
              src="../../../public/custom/images/check.svg"
              alt="Checket image"
            />
          </div>
          <div
            class="check-box-label"
            @click.stop="offerToggle('isDataFullAndCorrect')"
          >
            <span>{{ $t("message.signUp.isDataFullAndCorrect.text") }}</span>
          </div>
        </div> -->
        <div class="offer-item">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isAgreeWithServiceRules }"
            @click.stop="offerToggle('isAgreeWithServiceRules')"
          >
            <img
              v-if="isAgreeWithServiceRules"
              class="check-box-image"
              src="../../../public/custom/images/check.svg"
              alt="Checket image"
            />
          </div>
          <div
            class="check-box-label"
            @click.stop="offerToggle('isAgreeWithServiceRules')"
          >
            <span>{{ $t("message.signUp.isAgreeWithServiceRules.text") }}</span>
            <a class="check-box-link" href="/static/documents/active/Правила работы Сервиса.pdf" target="_blank">{{
              $t("message.signUp.isAgreeWithServiceRules.link")
            }}</a>
            <span>{{ $t("message.signUp.isAgreeWithServiceRules.secondText") }}</span>
            <a class="check-box-link" href="/static/documents/active/Политика обработки персональных данных.pdf" target="_blank">{{
              $t("message.signUp.isAgreeWithServiceRules.secondLink")
            }}</a>
          </div>
        </div>

        <div class="offer-item">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isAgreeWithServiceAgreement }"
            @click.stop="offerToggle('isAgreeWithServiceAgreement')"
          >
            <img
              v-if="isAgreeWithServiceAgreement"
              class="check-box-image"
              src="../../../public/custom/images/check.svg"
              alt="Checket image"
            />
          </div>
          <div
            class="check-box-label"
            @click.stop="offerToggle('isAgreeWithServiceAgreement')"
          >
            <span>{{
              $t("message.signUp.isAgreeWithServiceAgreement.textFirst")
            }}</span>
            <a class="check-box-link" href="/static/documents/active/Договор возмездного оказания услуг.pdf" target="_blank">{{
              $t("message.signUp.isAgreeWithServiceAgreement.link")
            }}</a>
            <span>{{
              $t("message.signUp.isAgreeWithServiceAgreement.textSecond")
            }}</span>
          </div>
        </div>

        <div class="offer-item">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isAgreeWithPersonalDataActions }"
            @click.stop="offerToggle('isAgreeWithPersonalDataActions')"
          >
            <img
              v-if="isAgreeWithPersonalDataActions"
              class="check-box-image"
              src="../../../public/custom/images/check.svg"
              alt="Checket image"
            />
          </div>
          <div
            class="check-box-label"
            @click.stop="offerToggle('isAgreeWithPersonalDataActions')"
          >
            <span>{{
              $t("message.signUp.isAgreeWithPersonalDataActions.textFirst")
            }}</span>
            <a class="check-box-link" target="_blank" href="/static/documents/active/Политика обработки персональных данных.pdf">{{
              $t("message.signUp.isAgreeWithPersonalDataActions.link")
            }}</a>
            <span>{{
              $t("message.signUp.isAgreeWithPersonalDataActions.textSecond")
            }}</span>
          </div>
        </div>
        <!-- <div class="offer-item" v-if="userData.role === 'borrower'">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isAgreeToGetBankReport }"
            @click.stop="offerToggle('isAgreeToGetBankReport')"
          >
            <img
              v-if="isAgreeToGetBankReport"
              class="check-box-image"
              src="../../../public/custom/images/check.svg"
              alt="Checket image"
            />
          </div>
          <div
            class="check-box-label"
            @click.stop="offerToggle('isAgreeToGetBankReport')"
          >
            <span>{{
              $t("message.signUp.isAgreeToGetBankReport.textFirst")
            }}</span>
            <span>{{
              $t("message.signUp.isAgreeToGetBankReport.link")
            }}</span>
            <span>{{
              $t("message.signUp.isAgreeToGetBankReport.textSecond")
            }}</span>
          </div>
        </div> -->
      </div>

      <div class="sign-up-block sign-up-block-completion">
        <div class="required-fields-block">
          <div class="required-fields-title">
            {{ $t('message.signUp.requiredFields.title') }}
          </div>
          <ul class="required-fields-list">
            <li
              v-for="(field, index) in requiredFieldsChecklist"
              :key="index"
              class="required-fields-item"
              :class="{ 'required-fields-item--complete': field.isComplete }"
            >
              {{ field.label }}
            </li>
          </ul>
        </div>
        <div class="btn-block">
          <div class="btn-container">
            <base-button
              :text="$t('message.signUp.signUpBtn')"
              @click="signUp()"
              :disabled="isSignUpButtonDisabled"
              :isLoading="isSignUpBtnLoading"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseDatepicker from "../../components/base/BaseDatepicker.vue";
import BaseSelect from "../../components/base/BaseSelect.vue";
import BaseRadioButton from "../../components/base/BaseRadioButton.vue";
import BasePasswordInput from "../../components/base/BasePasswordInput.vue";
import BaseTextInput from "../../components/base/BaseTextInput.vue";
import BaseTextareaInput from "../../components/base/BaseTextareaInput.vue";
import SignUpDocumentsUploader from "../../components/common/SignUpDocumentsUploader.vue";

import CONSTANTS from '../../constants/constants';
import { getEmptyLivingAddress, canRegisterAsBorrower } from '../../helpers/signUp';

import signUpValidationMixin from '../../mixins/signUp/signUpValidationMixin';
import metaEventMixin from '../../mixins/metaEventMixin';

import { mapGetters } from 'vuex';

function readCookie(name) {
  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(prefix));

  return cookie
    ? decodeURIComponent(cookie.slice(prefix.length))
    : null;
}

function normalizeRegistrationProofValue(value) {
  return String(value ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/gu, ' ')
    .toUpperCase();
}

function encodeBase64Url(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

async function createRegistrationProof(msiProfile) {
  const context = readCookie('_ui_ctx');

  if (!context) {
    throw new Error('Registration context is missing');
  }

  const timestamp = Math.floor(Date.now() / 1000);
  const name = typeof msiProfile.name_ru === 'string'
    ? JSON.parse(msiProfile.name_ru)
    : (msiProfile.name_ru || {});
  const message = [
    'v1',
    timestamp,
    normalizeRegistrationProofValue(msiProfile.public_id),
    normalizeRegistrationProofValue(msiProfile.national_id_number),
    normalizeRegistrationProofValue(name.family_name_ru),
    normalizeRegistrationProofValue(name.given_name_ru),
    normalizeRegistrationProofValue(name.middle_name_ru),
  ].join('\n');
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(context),
    {
      name: 'HMAC',
      hash: 'SHA-256',
    },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(message),
  );

  return `v1.${timestamp}.${encodeBase64Url(signature)}`;
}

export default {
  name: "SignUp",
  mixins: [signUpValidationMixin, metaEventMixin],
  inject: ['$httpService'],
  components: {
    BaseDatepicker,
    BaseSelect,
    BaseRadioButton,
    BasePasswordInput,
    BaseTextInput,
    BaseTextareaInput,
    SignUpDocumentsUploader,
  },
  async created() {
    const { code } = this.$route.query;
    
    code ? await this.$store.dispatch('getMsiProfile', code) : this.$router.push({ name: 'main' });

    this.userData = {...this.msiData};
    this.enforceBorrowerRole();
  },
  data() {
    return {
      CONSTANTS,
      isAddressLivingAndRegistrationTheSame: true,
      isDataFullAndCorrect: true,
      isAgreeWithServiceRules: false,
      isAgreeWithPersonalDataActions: false,
      isAgreeWithServiceAgreement: false,
      isAgreeToGetBankReport: true,
      work_info: {
        employment_type: '',
        scope_of_employment_type: '',
        organization_name: '',
        organization_unp: '',
        job_position_type: '',
        job_position_name: '',
        workExperience: ''
      },
      extra_user_info: {
        education: '1',
        drivers_license: false,
        car_owner: false,
        real_estate_owner: false,
        family_status: '1',
        dependens_number: '',
        child_expenses: false,
        total_income: ''
      },
      userData: {
        name_ru: {
          family_name_ru: '',
          given_name_ru: '',
          middle_name_ru: '',
        },
          id_document: {
          type: '3',
          authority: ''
        },
        role: 'borrower',
        codeword: ''
      },
      living_address: getEmptyLivingAddress(),
      livingAddressFieldKeys: [
        'country',
        'region',
        'district',
        'locality_type',
        'locality',
        'street_type',
        'street',
        'house',
        'building',
        'apartment',
        'postal_code',
      ],
      car_passport_files: [],
      real_estate_files: [],
      message_id: '',
      isSignUpBtnLoading: false
    };
  },
  computed: {
    ...mapGetters(['msiData', 'signUpDisabledFields', 'isLoading']),
    isSignUpButtonDisabled() {
      return !this.isOffersAccepted || this.isMainDataIncorrect || this.isJobDataIncorrect ||
        this.isIdDataIncorrect || this.isContactDataIncorrect || this.isAddressDataIncorrect || this.isHasValidationsErrors ||
        this.isExtraDataIncorrect;
    },
    isHasValidationsErrors() {
      return this.errors.items.length > 0;
    },
    isOtherJobPositionType() {
      const otherJobPositionType = '8';
      return this.work_info.job_position_type === otherJobPositionType;
    },
    isEmploymentWorkFieldsDisabled() {
      return this.isEmploymentTypeWithDisabledWorkFields(this.work_info.employment_type);
    },
    isTotalIncomeDisabled() {
      return this.work_info.employment_type === '3';
    },
    canRegisterAsBorrower() {
      return canRegisterAsBorrower(this.userData);
    },
  },
  watch: {
    'userData.citizenship'() {
      this.enforceBorrowerRole();
    },
    'userData.id_document.series'() {
      this.enforceBorrowerRole();
    },
    'work_info.employment_type'(value, previousValue) {
      if (value === '3') {
        this.setDisabledTotalIncome();
      } else if (previousValue === '3') {
        this.setDefaultTotalIncome();
      }

      if (this.isEmploymentTypeWithDisabledWorkFields(value)) {
        this.setDisabledEmploymentWorkFields();
        return;
      }

      if (this.isEmploymentTypeWithDisabledWorkFields(previousValue)) {
        this.setDefaultEmploymentWorkFields();
      }
    },
  },
  methods: {
    isEmploymentTypeWithDisabledWorkFields(employmentType) {
      const disabledEmploymentTypes = ['3', '4', '7', '10'];
      return disabledEmploymentTypes.includes(employmentType);
    },
    enforceBorrowerRole() {
      if (!this.canRegisterAsBorrower && this.userData.role === 'borrower') {
        this.userData.role = 'lender';
      }
    },
    setRole(role) {
      if (role === 'borrower' && !this.canRegisterAsBorrower) {
        return;
      }

      this.userData.role = role;
    },
    addressToggle() {
      this.isAddressLivingAndRegistrationTheSame = !this.isAddressLivingAndRegistrationTheSame;

      if (this.isAddressLivingAndRegistrationTheSame) {
        this.living_address = getEmptyLivingAddress();
      }
    },
    offerToggle(offerItem) {
      return (this[offerItem] = !this[offerItem]);
    },
    setDisabledEmploymentWorkFields() {
      this.work_info.scope_of_employment_type = '16';
      this.work_info.organization_name = '-';
      this.work_info.job_position_type = '9';
      this.work_info.job_position_name = '';
      this.work_info.workExperience = '0';

      this.errors.remove('nameOfOrganization');
      this.errors.remove('post');
      this.errors.remove('otherPositionType');
      this.errors.remove('workExperience');
    },
    setDisabledTotalIncome() {
      this.extra_user_info.total_income = '0';

      this.errors.remove('totalIncome');
    },
    setDefaultTotalIncome() {
      this.extra_user_info.total_income = '';
    },
    setDefaultEmploymentWorkFields() {
      this.work_info.scope_of_employment_type = '';
      this.work_info.organization_name = '';
      this.work_info.job_position_type = '';
      this.work_info.job_position_name = '';
    },
    setCarOwner(value) {
      this.extra_user_info.car_owner = value;

      if (!value) {
        this.car_passport_files = [];
      }
    },
    setRealEstateOwner(value) {
      this.extra_user_info.real_estate_owner = value;

      if (!value) {
        this.real_estate_files = [];
      }
    },
    hasSignUpUploadFiles() {
      return this.car_passport_files.length > 0 || this.real_estate_files.length > 0;
    },
    buildSignUpFormData(payload) {
      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        const value = payload[key];

        if (value === undefined || value === null || value === '') {
          return;
        }

        if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });

      this.car_passport_files.forEach((item) => {
        formData.append('car_passport_files', item.file);
      });

      this.real_estate_files.forEach((item) => {
        formData.append('real_estate_files', item.file);
      });

      return formData;
    },
    async getOtpCode() {
        try {
          const response = await this.$httpService.auth.getOtpCode(this.userData.phone_number, 'registration');

          this.message_id = response.data.message_id;
          this.$toast.success(this.$t('message.signIn.otpToastMsg'));
        } catch (error) {
          this.$router.push({name: 'SignIn'});
        }
    },
    async signUp() {
      this.isSignUpBtnLoading = true;

      try {
        const proof = await createRegistrationProof(this.userData);
        const otp = +this.userData.otp;
        const login = this.userData.phone_number;
        const living_address = this.isAddressLivingAndRegistrationTheSame ? undefined : this.living_address;
        const extra_user_info = this.userData.role === 'borrower' ? this.extra_user_info : undefined;
        const agreePersonalDataProcessing = this.isAgreeWithPersonalDataActions;
        const payload = {
          ...this.userData,
          work_info: this.work_info,
          otp,
          login,
          extra_user_info,
          living_address,
          agreePersonalDataProcessing,
        };
        const requestPayload = this.hasSignUpUploadFiles()
          ? this.buildSignUpFormData(payload)
          : payload;
        const response = await this.$httpService.msi.signUp(requestPayload, proof);

        const token = response.data.token;
        localStorage.setItem('token', token);

        this.sendMetaEvent('track', 'CompleteRegistration');
        this.isSignUpBtnLoading = false;
        this.$router.push({name: 'profile'});
      } catch (error) {
        this.isSignUpBtnLoading = false;
      }
    },
    goToMain() {
      this.$router.push({ name: 'main' });
    }
  },
};
</script>

<style lang="scss" scoped>
.sign-up-wrapper {
  @include base-page-wrapper;

  background-color: var(--sign-up-wrapper-bgc);
}

.sign-up-header {
  width: 100%;
  height: 90px;
  padding: 0 5%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--sign-up-header-bgc);
}

.logo {
  width: 150px;
  height: 31px;
  cursor: pointer;
}

.header-icon {
  height: 100%;
  max-width: 60%;
}

.sign-up-container {
  width: 1146px;
  max-width: 95%;
  padding: 70px 0 100px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.title {
  margin-bottom: 20px;
  font-family: "Montserrat";
  font-weight: 700;
  font-size: 40px;
  line-height: 125%;
  text-align: center;
  color: var(--sign-up-title-color);
}

.description {
  width: 356px;
  max-width: 80%;
  margin-bottom: 45px;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  text-align: center;
  color: var(--sign-up-desc-color);
}

.sign-up-block {
  width: 100%;
  margin-bottom: 60px;
  display: flex;
  flex-direction: column;
}

.sign-up-block-numbered,
.sign-up-block-agreements,
.sign-up-block-completion {
  padding: 32px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  border-radius: 16px;
  background-color: #ffffff;
  box-shadow: 0 10px 30px rgba(128, 128, 128, 0.2);
}

.sign-up-block-title {
  display: flex;
  align-items: flex-end;
  margin-bottom: 40px;
}

.step-block {
  height: 45px;
  width: 45px;
  margin-right: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: var(--step-block-bgc);
  color: var(--step-block-color);
  font-weight: 600;
  font-size: 26px;
  line-height: 35px;
}

.step-block-desc {
  font-weight: 600;
  font-size: 26px;
  line-height: 42px;
  color: var(--step-block-desc-color);
}

.sign-up-block-container {
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.row-block-container {
  flex-direction: column;
  justify-content: flex-start;
}

.middle-input-container {
  width: 30%;
  min-width: 343px;
}

.large-input-container {
  width: 65%;
  min-width: 744px;
}

.same-address-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 20px;
}

.same-address-title {
  margin-left: 10px;
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #333333;
  cursor: pointer;
}

.living-address-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 20px;
}

.living-address-field {
  min-width: 0;
  width: 100%;
}

.middle-double-container {
  width: 30%;
  min-width: 343px;
  display: flex;
  justify-content: space-between;
}

.small-input-container {
  width: calc(50% - 10px);
}

.input-wrapper {
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
}

.input-label {
  @include base-input-label;
}

.extra-data-radiobtn-input-label {
  margin-bottom: 0;
}

.input-block {
  @include base-input-block;
}

.gender-block {
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
}

.role-block {
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
}

.gender-item,
.role-item {
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #333333;
}

.gender-item:first-of-type,
.role-item:first-of-type {
  margin-right: 37px;
}

.gender-name {
  margin-left: 10px;
  opacity: 0.5;
}

.role-item-desc {
  margin-left: 10px;
  cursor: pointer;
}

.role-block .role-item-desc {
  display: flex;
  flex-direction: column;
}

.role-block .role-item-title {
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #333333;
}

.role-block .role-item-subtitle {
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #333333;
  opacity: 0.7;
}

.role-block .role-item--disabled {
  opacity: 0.5;
}

.role-block .role-item--disabled .role-item-desc {
  cursor: default;
}

.datepicker-block {
  width: 100%;
  height: 42px;
  border-radius: var(--base-input-border-radius);
  position: relative;
}


.otp-block {
  width: 50%;
  height: 42px;
  border-radius: var(--base-input-border-radius);
}

.invalid-input-block {
  border: 1px solid var(--base-input-invalid-border-bgc);
}

.base-input {
  @include base-input;
}

.input-error-block {
  @include base-input-error-block;
}

.middle-block-container {
  width: 65%;
}

.same-address-block,
.offer-block,
.offer-item {
  margin-bottom: 30px;
  display: flex;
  align-items: flex-start;
}

.check-box-btn {
  @include base-check-box;
}

.check-box-label {
  @include base-check-box-label;
}

.citizenship-block {
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
}

.radio-btn-block {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-end;
  opacity: 0.8;
}

.radio-btn-label {
  @include base-radio-btn-label;
  margin-left: 10px;
  margin-bottom: 3px;
  cursor: default;
}

.radio-btn-disabled {
  cursor: default;
}

.notice-block {
  margin-bottom: 25px;
  font-weight: 400;
  font-size: 18px;
  line-height: 25px;
  color: var(--sign-up-dovks-upload-notice-color);
}

.key-word-warning {
  margin-top: 10px;
  margin-bottom: 20px;
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: var(--error-message-color);
}

.required-fields-block {
  width: 100%;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.required-fields-title {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 25px;
  color: #333333;
}

.required-fields-list {
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 24px;
}

.required-fields-item {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: var(--error-message-color);
}

.required-fields-item--complete {
  color: #41bf7b;
}

.btn-container {
  width: 300px;
  height: 65px;
}

.btn-block {
  display: flex;
}

.send-otp-block {
  margin-top: 3px;
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  letter-spacing: 0.01em;
  text-decoration-line: underline;
  color: #41BF7B;
  cursor: pointer;
  font-family: 'Open Sans';
  user-select: none;
}

.half-middle-input-block ::v-deep {
  width: 50%;
}

.row-radibtn-container {
  display: flex;
  align-items: center;
}

.extra-radiobtn-block {
  display: flex;
}

.car-passport-upload-container {
  margin-top: 0;
}

.row-radiobtn-container {
  margin-bottom: 30px;
}

.mb-20 {
  margin-bottom: 20px;
}

@media screen and (max-width: 800px) {
  .large-input-container {
    width: 100%;
    min-width: 100%;
  }
}

@media screen and (max-width: 1100px) {
  .living-address-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .required-fields-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 740px) {
  .extra-data-radiobtn-input-label {
    margin-bottom: 20px;
  }

  .living-address-grid {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 600px) {
  .sign-up-block-numbered,
  .sign-up-block-agreements,
  .sign-up-block-completion {
    padding: 20px;
    border-radius: 12px;
  }

  .required-fields-list {
    grid-template-columns: 1fr;
  }

  .middle-input-container {
    width: 100%;
    min-width: 100%;
  }

  .btn-block {
    align-items: center;
  }

  .step-block-desc {
    width: calc(100% - 65px);
  }

  .sign-up-block-title {
    align-items: flex-start;
  }

  .same-address-title {
    width: calc(100% - 28px);
  }
}
</style>
